import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type UserPublicV2 = NonNullable<
  paths['/v2/users/public/{id}']['get']['responses'][200]['content']['application/json']['data']
>;

export async function getUserPublicV2(id: number): Promise<UserPublicV2> {
  const { data } = await fetchClient.GET('/v2/users/public/{id}', {
    params: { path: { id } },
  });

  if (!data?.data) {
    throw new Error('User not found');
  }

  return data.data as UserPublicV2;
}

type GetUserPublicV2QueryOptionsConfig = {
  /**
   * Whether a profile that failed to load is requested again when another component mounts to
   * read it. On by default so a screen opened after a brief failure recovers.
   */
  retryFailedOnMount?: boolean;
};

/**
 * A screen that swaps a component in and out while this query loads passes
 * `retryFailedOnMount: false`. The inquiry screen shows a spinner while the profile loads and
 * the profile panel once it settles, so a retry on mount restarts the load, the swap mounts
 * the panel again, and the cycle never ends. A missing profile does not appear by asking again.
 */
export const getUserPublicV2QueryOptions = (
  id?: number | string,
  { retryFailedOnMount = true }: GetUserPublicV2QueryOptionsConfig = {}
) =>
  queryOptions<UserPublicV2>({
    queryKey: ['usersPublicV2', id],
    queryFn: () => getUserPublicV2(Number(id)),
    staleTime: ONE_MINUTE,
    enabled: !!id,
    retryOnMount: retryFailedOnMount,
  });
